import {
  IMultiSportCompetition,
  ParticipantDetail,
  Result,
  IConfigEntityLinks,
  SDKDateOptions,
  ShortNameType,
} from '../../../../packages/types';
import { LIVE, NOT_STARTED } from '../../../../packages/constants/statusTypes';
import { assetsPlaceholder } from '../../../../packages/constants/assetsPlaceholder';
import {
  LinkCreationService,
  getLabelValue,
  getLivescoreStatusForDisplay,
  getNameByStatus,
  getStatusDate,
  getResultLabel,
  getCompetitionLogo,
} from '@sportal365/smp-widget-utils';
import dayjs from 'dayjs';
import { TARGET_BLANK, TARGET_SELF } from '../../../../packages/constants/windowTarget';
import { IMultiSportLivescoreDataLabels, ResultTypes } from '../../../../packages/types/multisport';
import { COMPETITION_ENTITY, STANDINGS_ENTITY } from '../../../constants/entityTypes';

interface INormalizeEvents {
  competitions?: IMultiSportCompetition[];
  nameType?: ShortNameType | null;
  dataLabels?: IMultiSportLivescoreDataLabels | null;
  dataEntityLinks?: IConfigEntityLinks | null;
  sdkOptions: SDKDateOptions;
}

const getLabels = (data: ParticipantDetail, status: string, dataLabels?: IMultiSportLivescoreDataLabels | null) => {
  if (status === NOT_STARTED) {
    return [dataLabels?.score ?? 'S'];
  }

  return data.results?.map(({ type }) => getResultLabel(type.code, dataLabels)).filter((label) => label !== '');
};

const getResults = (results: Result[], status: string, dataLabels?: IMultiSportLivescoreDataLabels | null) => {
  if (status === NOT_STARTED || results.length < 1) {
    return [{ label: dataLabels?.score ?? 'S', value: '-', isFinal: false, isWinner: false }];
  }

  return results
    ?.map(({ value, winner, type }) => ({
      label: getResultLabel(type.code, dataLabels),
      value: value ?? '-',
      isWinner: winner === 'YES' ? true : false,
      isFinal: type.code === ResultTypes.FINAL_RESULT ? true : false,
    }))
    .filter(({ label }) => label !== '');
};

export const normalizeEventsData = ({ competitions, nameType, dataLabels, dataEntityLinks, sdkOptions }: INormalizeEvents) => {
  const linkService = dataEntityLinks && new LinkCreationService(dataEntityLinks);
  const target = dataEntityLinks?.configuration?.newWindow ? TARGET_BLANK : TARGET_SELF;

  if (!competitions) {
    return [];
  }

  return competitions?.map(({ competition, events }) => ({
    competition: {
      id: competition.id,
      title: competition.name,
      slug: competition.slug,
      target: target,
      subTitle: competition?.country?.name ? competition.country.name : competition.name,
      competitionLogo: getCompetitionLogo(competition),
      competitionUrl: linkService?.createEntityUrl(competition?.id ?? '', COMPETITION_ENTITY, '', competition?.slug) ?? '#',
      standingsLabel: getLabelValue(dataLabels?.standings, 'Standings'),
      standingsUrl: linkService?.createEntityUrl(competition?.id ?? '', STANDINGS_ENTITY, '', competition?.slug) ?? '#',
    },
    events: events.map(({ id, start_time, status, participant_details, odds, slug, status_type, elapsed }) => ({
      id: id,
      date: start_time && status_type ? `${getStatusDate(start_time, status_type, sdkOptions)}` : '',
      status: getLivescoreStatusForDisplay(status),
      startTime: status_type === NOT_STARTED ? dayjs(start_time).format('HH:mm') : '',
      isLive: status_type === LIVE ? true : false,
      elapsed: elapsed ?? '',
      statusType: status_type,
      eventUrl:
        linkService?.createMatchUrl(
          id ?? '',
          participant_details[0]?.participant ?? '',
          participant_details[1]?.participant ?? '',
          slug ?? '',
        ) ?? '#',
      target: target,
      participants: participant_details.map(({ participant, results }) => ({
        id: participant.id,
        logo: participant?.display_asset?.url || assetsPlaceholder.team,
        name: getNameByStatus({ ...participant, status: nameType }),
        isWinner: participant.winner === 'YES' ? true : false,
        results: getResults(results, status_type, dataLabels),
        isNational: competition.region === 'NATIONAL',
      })),
      labels: getLabels(participant_details[0], status_type, dataLabels),
      odds: odds !== null && odds?.length > 0 ? odds : [],
    })),
  }));
};
